import { Fragment } from 'react';
import PropTypes from 'prop-types';

import { useSideMsgDock } from '#/modules/commonComponents/SideMsgDockContext';
import SideMsgDockTitleActions from '#/modules/commonComponents/SideMsgDockTitleActions';

function getValue(valueConfig, data) {
  let result;

  if (typeof valueConfig === 'function') {
    result = valueConfig(data);
  } else if (typeof valueConfig === 'string') {
    result = valueConfig.split('.').reduce((obj, key) => obj?.[key], data);
  } else {
    result = valueConfig;
  }

  if (result === null || result === undefined || result === '') {
    return '-';
  }

  return result;
}

/**
 * L2 — 側欄標題、捲動、empty 狀態。
 * 列表頁傳入 `displayData`（列資料）；禁止為 SideMsg 另打 get_detail。
 *
 * @see docs/UI_LAYOUT.md
 */
const SideMsg = ({
  displayData,
  title = '詳細資訊',
  titleValue,
  fields,
  blocks,
  emptyMessage = '請先選擇一筆資料',
  children,
}) => {
  const { enableDockModes } = useSideMsgDock();

  function getTitleValue() {
    if (titleValue) {
      return getValue(titleValue, displayData);
    }

    const defaultValue = displayData?.name || displayData?.code;
    return defaultValue || '-';
  }

  function renderFields(fieldList) {
    if (!fieldList || fieldList.length === 0) {
      return null;
    }

    return fieldList.map((field, index) => {
      if (field.condition && !field.condition(displayData)) {
        return null;
      }

      const fieldKey = field.key != null && field.key !== ''
        ? String(field.key)
        : `field-${index}`;

      if (field.render) {
        return (
          <Fragment key={fieldKey}>
            {field.render(displayData)}
          </Fragment>
        );
      }

      return (
        <div key={fieldKey} className="side_msg-row">
          <h3>{field.label}</h3>
          <p>{getValue(field.value, displayData)}</p>
        </div>
      );
    });
  }

  function renderBlocks() {
    if (blocks && blocks.length > 0) {
      return blocks.map((block, blockIndex) => {
        if (block.condition && typeof block.condition === 'function' && !block.condition(displayData)) {
          return null;
        }

        return (
          <div key={blockIndex} className="side_msg-content-block">
            {renderFields(block.fields)}
            {block.children && block.children(displayData)}
          </div>
        );
      });
    }

    if (fields && fields.length > 0) {
      return <div className="side_msg-content-block">{renderFields(fields)}</div>;
    }

    return null;
  }

  return (
    <div className="side_msg_container min-h-0 xl:h-full">
      <div className="side_msg">
        <div className="side_msg_title">
          {enableDockModes ? (
            <>
              <div className="side_msg_title-main">
                <h3>{title}</h3>
                <p>{getTitleValue()}</p>
              </div>
              <SideMsgDockTitleActions />
            </>
          ) : (
            <>
              <h3>{title}</h3>
              <p>{getTitleValue()}</p>
            </>
          )}
        </div>
        <div className="side_msg-content">
          {displayData ? (
            children && typeof children === 'function' ? (
              children(displayData)
            ) : (
              renderBlocks()
            )
          ) : (
            <div className="side_msg-empty">
              <p>{emptyMessage}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

SideMsg.propTypes = {
  displayData: PropTypes.object,
  title: PropTypes.string,
  titleValue: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  fields: PropTypes.array,
  blocks: PropTypes.array,
  emptyMessage: PropTypes.string,
  children: PropTypes.func,
};

export default SideMsg;
